import { useState } from 'react';
import { useStore } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { alpha } from '@mui/material/styles';
import { CSSProperties } from 'react';
import { shallow } from 'zustand/shallow';
import { UseHandleConnectionProps } from './types.d';

export const useHandleConnection = ({
  handleId,
  validSourceHandles,
  isActive = false,
  colorActive,
  side,
}: UseHandleConnectionProps) => {
  const { user } = useAuthStore(state => state);
  const [isHovered, setIsHovered] = useState(false);

  const { isDraggingConnection, sourceHandleId } = useStore(
    s => ({
      isDraggingConnection: s.connectionNodeId !== null,
      sourceHandleId: s.connectionStartHandle?.handleId,
    }),
    shallow,
  );

  const isHandlesSameType = sourceHandleId === handleId;
  const isValidSourceHandle =
    sourceHandleId &&
    validSourceHandles.some(handle => sourceHandleId?.startsWith(handle));
  const isDark = user?.theme === 'dark';
  const isDropTarget =
    isDraggingConnection &&
    isHovered &&
    isValidSourceHandle &&
    !isHandlesSameType;

  const backgroundColor =
    isDropTarget || isActive ? colorActive?.color : isDark ? '#1D2939' : '#fff';

  const HANDLE_SIZE = 32;
  const HALF_SIZE = HANDLE_SIZE / 2;

  const baseStyle: CSSProperties = {
    boxSizing: 'border-box',
    width: `${HANDLE_SIZE}px`,
    height: `${HANDLE_SIZE}px`,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [side === 'left' ? 'left' : 'right']: `-${HALF_SIZE}px`,
    transform: `translateY(-${HALF_SIZE}px)`,
    ...(isDraggingConnection &&
      !isValidSourceHandle && { cursor: 'not-allowed' }),
  };

  const visualStyle: CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor,
    border: isDropTarget
      ? `2px solid ${colorActive?.color}`
      : isActive
        ? '2px solid transparent'
        : '2px solid #667085',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transform: `scale(${isDropTarget ? '1.25' : '1'})`,
    transformOrigin: 'center center',
    boxShadow: isDropTarget
      ? `0 0 0 4px ${alpha(
          colorActive?.color || '#3650DE',
          0.25,
        )}, 0 0 12px ${alpha(colorActive?.color || '#3650DE', 1)}`
      : undefined,
    transition:
      isDropTarget || isActive
        ? 'background-color 100ms ease, border-color 100ms ease, box-shadow 100ms ease, transform 100ms ease'
        : 'none',
    pointerEvents: 'none',
  };

  const handleMouseEnter = () => {
    if (isDraggingConnection && !isHandlesSameType) setIsHovered(true);
  };

  const handleMouseLeave = () => setIsHovered(false);

  return {
    isDropTarget,
    isDark,
    backgroundColor,
    baseStyle,
    visualStyle,
    handleMouseEnter,
    handleMouseLeave,
  };
};
